// ---------------------------------------------------------------------------
// Screenshot upload helper for the feedback form.
//
// The visitor uploads their OWN image (file picker / paste / drag) so they can
// show exactly what they mean — we just down-scale + JPEG-compress it to a data-
// URL that fits the backend cap. There is deliberately NO automatic page
// capture: auto-screenshotting a visitor's view of a third-party site is a data-
// protection problem we don't want, so the screenshot is always something the
// visitor explicitly chose to attach. Mirrors the Dashboard's
// FeedbackButton.fileToDataUrl so both surfaces behave identically.
//
// Feeds the existing /v1/feedback `screenshot` field (stored in KV, ≤ 1.2 MB,
// data:image/ only — see packages/api/src/routes/feedback.ts).
// ---------------------------------------------------------------------------

// Backend cap is 1.2 MB; stay safely under it (base64 is ~1.33× the bytes).
export const MAX_SHOT_BYTES = 1_100_000;

/** Down-scale a canvas to `maxEdge` long-edge and JPEG-encode, stepping the
 *  quality down until the data-URL fits under MAX_SHOT_BYTES. */
function canvasToJpeg(
  canvas: HTMLCanvasElement,
  maxEdge: number,
  qualities: number[],
): string {
  let src: HTMLCanvasElement = canvas;
  const longEdge = Math.max(canvas.width, canvas.height);
  if (longEdge > maxEdge) {
    const s = maxEdge / longEdge;
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(canvas.width * s));
    c.height = Math.max(1, Math.round(canvas.height * s));
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, c.width, c.height);
      src = c;
    }
  }
  let url = src.toDataURL('image/jpeg', qualities[0]);
  for (let i = 1; i < qualities.length && url.length > MAX_SHOT_BYTES; i++) {
    url = src.toDataURL('image/jpeg', qualities[i]);
  }
  return url;
}

/**
 * Read a visitor-picked image file and return a compressed JPEG data-URL.
 * Long edge capped at 1600 px; quality stepped down until it fits. Mirrors the
 * Dashboard's FeedbackButton.fileToDataUrl so both surfaces behave identically.
 */
export async function fileToDataUrl(file: File): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('image-decode-failed'));
      im.src = objectUrl;
    });
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no-2d-context');
    ctx.drawImage(img, 0, 0, w, h);
    return canvasToJpeg(canvas, 1600, [0.85, 0.7, 0.55]);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
